import { z } from 'zod';

/**
 * Tipi backup DB reali — pgBackRest.
 *
 * Sostituiscono il vecchio modello EDG (dumpall/basebackup/etcd → R2
 * bucket edgvoip-db-backups) mai deployato. La dash "Gestione Database"
 * del Master legge questi tipi dal backend, alimentati dallo snapshot
 * periodico `master.pgbackrest_snapshot` (cron collector DB-1) + scrape
 * opzionale del pgbackrest_exporter.
 *
 * pgBackRest produce backup di tipo `full` / `diff` / `incr` su un repo
 * (qui Cloudflare R2), con WAL archiviati in continuo. Il restore e' PITR:
 * si ripristina un backup base e si replaya il WAL fino al target.
 */

/** Tipo di backup pgBackRest. */
export type PgBackrestBackupType = 'full' | 'diff' | 'incr';

/** Severita' anomalia backup, allineata agli alert observability. */
export type BackupAnomalySeverity = 'critical' | 'warning';

/** Stato sintetico complessivo del sottosistema backup. */
export type BackupOverallStatus = 'ok' | 'warning' | 'critical' | 'unknown';

/** Dimensioni (byte) di un singolo backup pgBackRest. */
export interface PgBackrestBackupSize {
  /** Dimensione logica del database al momento del backup. */
  database_bytes: number | null;
  /** Dimensione del backup (delta effettivo per diff/incr). */
  backup_bytes: number | null;
  /** Dimensione occupata nel repo (compressa). */
  repo_bytes: number | null;
}

/** Singolo backup pgBackRest (una entry di `info`). */
export interface PgBackrestBackup {
  /** Label pgBackRest, es. `20260921-140000F` / `...F_20260921-150000I`. */
  label: string;
  type: PgBackrestBackupType;
  /** Inizio backup (ISO 8601). */
  started_at: string;
  /** Fine backup (ISO 8601). */
  finished_at: string;
  /** Eta' del backup in secondi (rispetto a collected_at). */
  age_seconds: number;
  size: PgBackrestBackupSize;
  /** Label dei backup di riferimento (catena per diff/incr). */
  reference: string[];
  /** WAL di inizio/fine coperti da questo backup. */
  wal_start: string | null;
  wal_stop: string | null;
  /** True se la entry risulta errored/incompleta in pgBackRest. */
  errored: boolean;
}

/** Stato dell'archiviazione WAL per la stanza. */
export interface PgBackrestArchiveStatus {
  /** Ultimo WAL archiviato con successo. */
  last_archived_wal: string | null;
  /** Min/max WAL presenti nel repo per la timeline corrente. */
  min_wal: string | null;
  max_wal: string | null;
}

/** Stato della stanza pgBackRest. */
export interface PgBackrestStanza {
  name: string;
  /** Codice stato pgBackRest (0 = ok). */
  status_code: number;
  /** Messaggio stato pgBackRest. */
  status_message: string;
  /** Versione PostgreSQL della stanza. */
  pg_version: string | null;
  archive: PgBackrestArchiveStatus;
  backups: PgBackrestBackup[];
}

/** Oggetto reale nel repo R2 pgBackRest. */
export interface R2RepoObject {
  key: string;
  size_bytes: number;
  last_modified: string;
  age_seconds: number;
}

/** Summary persisted for every snapshot; object detail is retained only on the latest row. */
export interface R2RepoSummary {
  listed: boolean;
  object_count: number | null;
  total_bytes: number | null;
  newest_object_at: string | null;
  newest_object_age_seconds: number | null;
  wal_object_count: number | null;
  wal_bytes: number | null;
  backup_object_count: number | null;
  backup_bytes: number | null;
}

/** Change between lightweight R2 summaries, never computed from object history. */
export interface R2RepoTrend {
  sample_count: number;
  window_seconds: number | null;
  bytes_delta: number | null;
  objects_delta: number | null;
  bytes_per_hour: number | null;
  anomalous_growth: boolean;
}

/** Stato del dump logico complementare (backup-db.sh age+R2). */
export interface LogicalDumpStatus {
  enabled: boolean;
  healthy: boolean;
  last_run_at: string | null;
  last_run_age_seconds: number | null;
  last_error: string | null;
  expected_state: string;
  missing: string[];
}

/** Esito del gate periodico che verifica un restore non distruttivo. */
export interface RestoreGateStatus {
  enabled: boolean;
  healthy: boolean;
  last_checked_at: string | null;
  last_checked_age_seconds: number | null;
  last_error: string | null;
  expected_state: string;
  missing: string[];
  /** Optional only for compatibility with pre-upgrade collectors. */
  verifications?: RestoreVerificationSummary;
}

const verificationTimestamp = z.string().datetime({ offset: true }).nullable();
export const restoreVerificationRunSchema = z.object({
  run_id: z.string().min(1),
  kind: z.enum(['physical', 'logical']),
  status: z.enum(['pass', 'fail', 'skip', 'running']),
  started_at: verificationTimestamp,
  finished_at: verificationTimestamp,
  duration_seconds: z.number().finite().nonnegative().nullable(),
  node: z.string().nullable(),
  target: z.string().nullable(),
  last_error: z.string().nullable(),
});
export type RestoreVerificationRun = z.infer<typeof restoreVerificationRunSchema>;

export const restoreVerificationScheduleSchema = z.object({
  active: z.boolean(),
  observed_at: verificationTimestamp,
  next_at: verificationTimestamp,
  calendar: z.string().nullable(),
  reason: z.string().nullable(),
  last_trigger_at: verificationTimestamp.optional(),
});
export type RestoreVerificationSchedule = z.infer<typeof restoreVerificationScheduleSchema>;

export const restoreVerificationSummarySchema = z.object({
  physical: restoreVerificationRunSchema.nullable(),
  logical: restoreVerificationRunSchema.nullable(),
  physical_schedule: restoreVerificationScheduleSchema,
  logical_schedule: restoreVerificationScheduleSchema,
  history: z.array(restoreVerificationRunSchema),
  retention: z.enum(['eligible', 'blocked', 'unknown']),
});
export type RestoreVerificationSummary = z.infer<typeof restoreVerificationSummarySchema>;

/** Stato scrape pgbackrest_exporter (:9854). */
export interface PgBackrestExporterStatus {
  reachable: boolean;
  /** Eta' ultimo backup (s) dall'exporter, ridondante con info ma utile come cross-check. */
  last_backup_age_seconds: number | null;
  error: string | null;
}

/** SLA sintetico per i tre piani di copertura. */
export interface BackupSlaSummary {
  /** Ultimo full: eta' (s) + entro SLA (target_seconds). */
  full_age_seconds: number | null;
  full_sla_target_seconds: number;
  full_within_sla: boolean;
  /** Ultimo backup incrementale/diff: eta' + SLA. */
  incremental_age_seconds: number | null;
  incremental_sla_target_seconds: number;
  incremental_within_sla: boolean;
  /** Archiviazione WAL attiva (ultimo WAL entro grace). */
  wal_archiving_active: boolean;
  wal_last_archived_age_seconds: number | null;
}

/** Finestra di Point-In-Time-Recovery disponibile. */
export interface PitrWindow {
  /** Primo istante recuperabile (inizio del backup full piu' vecchio utile). */
  earliest_recoverable_at: string | null;
  /** Ultimo istante recuperabile (ultimo WAL archiviato). */
  latest_recoverable_at: string | null;
  /** Ampiezza finestra in secondi. */
  window_seconds: number | null;
}

/** Anomalia unificata del sottosistema backup. */
export interface BackupAnomaly {
  severity: BackupAnomalySeverity;
  code: string;
  message: string;
}

/** Risposta principale: stato backup reale per la dash. */
export interface BackupStatusResponse {
  overall: BackupOverallStatus;
  /** True se lo snapshot e' fresco (ponte cron DB-1 attivo). */
  bridge_healthy: boolean;
  /** Quando il cron DB-1 ha raccolto lo snapshot (ISO). */
  collected_at: string | null;
  /** Eta' dello snapshot in secondi (null se nessuno snapshot). */
  snapshot_age_seconds: number | null;
  node_name: string | null;
  stanza: PgBackrestStanza | null;
  /** Detailed listing from the current operational snapshot only. */
  r2_objects: R2RepoObject[];
  r2_summary: R2RepoSummary | null;
  r2_trend: R2RepoTrend | null;
  logical_dump: LogicalDumpStatus | null;
  restore_gate: RestoreGateStatus | null;
  exporter: PgBackrestExporterStatus | null;
  sla: BackupSlaSummary;
  pitr: PitrWindow;
  anomalies: BackupAnomaly[];
  /** Quando il backend ha calcolato questa risposta (ISO). */
  generated_at: string;
}

/** Target di un restore assistito. */
export type RestoreTargetKind = 'latest' | 'time' | 'backup_label';

/** Richiesta piano di restore (query string). */
export interface RestorePlanRequest {
  target_kind: RestoreTargetKind;
  /** ISO timestamp se target_kind='time'; label se 'backup_label'; ignorato se 'latest'. */
  value?: string;
}

/** Singolo passo operativo del restore (copia-incolla, NO exec da UI). */
export interface RestorePlanStep {
  order: number;
  title: string;
  /** Comando shell o azione da eseguire manualmente sul nodo. */
  command: string;
  /** Nodo dove eseguire (es. DB-1 / leader). */
  node: string;
  /** Nota/avvertenza per il passo. */
  note: string | null;
}

/** Risposta piano di restore assistito. */
export interface RestorePlanResponse {
  target_kind: RestoreTargetKind;
  resolved_target: string;
  /** Backup base scelto come punto di partenza del restore. */
  base_backup_label: string | null;
  /** True se la catena WAL copre il target richiesto. */
  wal_chain_complete: boolean;
  wal_chain_note: string | null;
  pitr: PitrWindow;
  /** Comando pgbackrest restore principale (informativo). `null` se il target non
   *  e' valido o la catena WAL e' incompleta: in tal caso non si emette comando eseguibile. */
  restore_command: string | null;
  steps: RestorePlanStep[];
  /** Avvertenze globali (es. operazione distruttiva, coordinamento cluster). */
  warnings: string[];
  generated_at: string;
}

/** Risposta sintesi salute per il banner anomalie della dash. */
export interface DbHealthSummaryResponse {
  overall: BackupOverallStatus;
  critical_count: number;
  warning_count: number;
  anomalies: BackupAnomaly[];
  generated_at: string;
}

/** Risposta del trigger backup assistito (nessuna esecuzione lato server). */
export interface AssistedBackupCommandResponse {
  /** Comando pgbackrest da eseguire manualmente su DB-1. */
  command: string;
  node: string;
  note: string;
}
